import { Backdrop, Button, CircularProgress, Typography } from '@mui/material';
import { AuthInput, FormCard, PrimaryButton } from '../login';
import { useTranslation } from '../../hooks/useTranslation';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { HOST_API } from '../../config/hosts';
import { Eye, EyeOff } from 'lucide-react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCookiesLogin } from '../../config/cookies';

export function ResetPasswordForm() {
  const { token } = useParams();
  const { cookies, removeCookie } = useCookiesLogin();

  const { t } = useTranslation();

  const [validToken, setValidToken] = useState(false);
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const isValidatingRef = useRef(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successSent, setSuccessSent] = useState(false);

  const validateToken = useCallback(async () => {
    if (isValidatingRef.current) return;
    isValidatingRef.current = true;
    setIsLoadingToken(true);

    try {
      const response = await fetch(`${HOST_API}/v1/auth/recovery/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        setValidToken(true);
      } else if (response.status === 401) {
        toast.error(t('messages.expiredToken'), { theme: 'dark' });
      } else {
        toast.error(t('messages.invalidToken'), { theme: 'dark' });
      }
    } catch (error) {
      toast.error(t('messages.invalidToken'), { theme: 'dark' });
    } finally {
      setIsLoadingToken(false);
      isValidatingRef.current = false;
    }
  }, [token, t]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleRecoverPassword();
    }
  };

  const handleRecoverPassword = async () => {
    if (isLoading) return;

    if (password !== confirmPassword) {
      toast.error(t('auth.passwordNotMatch'), { theme: 'dark' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${HOST_API}/v1/auth/recovery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
        body: JSON.stringify({
          password: password,
          token: token,
        }),
      });

      if (response.status === 200) {
        setSuccessSent(true);
        removeCookie('AUTH_TOKEN');
        localStorage.clear();
      } else {
        toast.warning(t('messages.passwordResetError'), { theme: 'dark' });
      }
    } catch (error) {
      toast.error(t('messages.passwordResetError'), { theme: 'dark' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!validToken) {
    return (
      <FormCard title={t('messages.invalidToken')}>
        <Typography sx={{ fontSize: 14, fontWeight: 400, color: '#575757', fontFamily: 'Ubuntu' }}>
          {t('messages.invalidTokenMessage')}
        </Typography>
      </FormCard>
    );
  }

  if (isLoadingToken) {
    return (
      <Backdrop open={isLoadingToken} style={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" sx={{ color: '#83d84d' }} />
      </Backdrop>
    );
  }

  return (
    <>
      {successSent ? (
        <FormCard>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '18px',
                borderRadius: '50%',
                backgroundColor: '#E9F7DF',
              }}
            >
              <CheckCircleIcon sx={{ color: '#83D84D', fontSize: 48 }} />
            </div>
            <Typography
              sx={{ fontSize: 24, fontWeight: 700, color: '#2C2C2C', fontFamily: 'Ubuntu' }}
            >
              {t('auth.passwordResetSuccess')}
            </Typography>

            <Typography
              sx={{ fontSize: 14, fontWeight: 400, color: '#575757', fontFamily: 'Ubuntu' }}
            >
              {t('auth.canAcess')}
            </Typography>

            <button
              type="button"
              onClick={() => {
                window.location.href = '/';
              }}
              className="w-full bg-white border border-[#83d84d] text-[#83d84d] hover:bg-[#f6fff0] active:bg-[#ecffe0] font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {t('auth.logIn')}
            </button>
          </div>
        </FormCard>
      ) : (
        <FormCard title={t('auth.recoverPassword')}>
          <AuthInput
            label={t('auth.newPassword')}
            id="loginPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder={t('auth.newPasswordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            rightSlot={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600 transition-colors mt-2"
              >
                {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
              </button>
            }
          />

          <AuthInput
            label={t('auth.confirmPassword')}
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder={t('auth.repeatPasswordPlaceholder')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            rightSlot={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="text-gray-400 hover:text-gray-600 transition-colors mt-2"
              >
                {showConfirmPassword ? <EyeOff size={24} /> : <Eye size={24} />}
              </button>
            }
          />

          <PrimaryButton onClick={handleRecoverPassword} disabled={isLoading}>
            {isLoading ? t('common.loading') : t('auth.recoverPassword')}
          </PrimaryButton>
        </FormCard>
      )}

      <Backdrop open={isLoading} style={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" sx={{ color: '#83d84d' }} />
      </Backdrop>
    </>
  );
}
